import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CircleAlert, Download, Fingerprint, Pause, Play, RefreshCw, Search, Users, X } from 'lucide-react';
import * as hr from '../api';
import Loader from '../components/Loader';
import { useHR } from '../HRContext';
import { DateInput, Select } from '../components/Controls';
import { download, toCSV } from '../format';
import { Chip, Kpi, iso } from './attendance/shared';
import { every, t12 } from './biometric/shared';

// Time & Attendance > Biometric Registrations > Live Punches: every punch the devices sent for a day, as it arrives.
// The device's own in/out state is shown for reference only: it is not trusted (some models always send the same one).

const COLS = [['Time', r => t12(r.time)], ['Employee ID', r => r.employee_no || ''], ['Name', r => r.name || ''], ['Department', r => r.department || ''],
  ['Device PIN', r => r.pin], ['Device', r => r.device], ['Site', r => r.site || ''], ['Verified By', r => r.verify || ''], ['Device State', r => r.device_status || ''],
  ['Work Day', r => r.work_date || ''], ['Attendance', r => attendance(r)[0]]];
// where the punch stands with the attendance register
const attendance = r => {
  if (r.discarded) return ['Discarded', 'gray'];
  if (r.suspect) return ['Held: device time', 'red'];
  if (!r.employee_id) return ['Unmatched', 'orange'];
  if (!r.applied) return ['Waiting', 'yellow'];
  if ((r.role || '').startsWith('Kept')) return ['Record kept', 'purple'];
  return [r.role === 'In' ? 'Clock-in' : r.role === 'Out' ? 'Clock-out' : r.role || 'Applied', r.role === 'Repeat' || r.role === 'Between' ? 'gray' : 'green'];
};
const VERIFY_TONE = { Face: 'purple', Fingerprint: 'teal', Card: 'orange', Password: 'gray', Palm: 'purple' };

export default function BiometricPunches() {
  const { notify } = useHR();
  const [f, setF] = useState({ d: iso(new Date()), device: '', matched: '' });
  const [q, setQ] = useState('');
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [live, setLive] = useState(true);
  const seen = useRef(null);
  const [fresh, setFresh] = useState(new Set());

  const load = useCallback(() => hr.getPunches(f).then(r => {
    // highlight punches that arrived since the last refresh (not on the first load of a day)
    const ids = new Set(r.rows.map(x => x.id));
    setFresh(seen.current ? new Set([...ids].filter(x => !seen.current.has(x))) : new Set());
    seen.current = ids;
    setD(r); setError('');
  }).catch(e => setError(e.message)), [f]);
  useEffect(() => { seen.current = null; load(); }, [load]);
  const isToday = f.d === iso(new Date());
  useEffect(() => (live && isToday ? every(10000, load) : undefined), [live, isToday, load]);

  const rows = useMemo(() => {
    if (!d) return [];
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return words.length ? d.rows.filter(r => words.every(w => [r.name, r.employee_no, r.pin, r.device, r.site].some(v => String(v || '').toLowerCase().includes(w)))) : d.rows;
  }, [d, q]);

  if (error && !d) return <div className="error">{error}</div>;
  if (!d) return <Loader title="Loading punches…" subtitle="Fetching the day's biometric punches" />;
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));

  return (
    <div className="mdr mds ael bio bdv">
      <div className="mdr-top">
        <div>
          <h2>Live Punches</h2>
          <p>Every punch the devices sent for the day, newest first, and what it did in attendance: first punch is the clock-in, last the clock-out. Times are the device's local time.</p>
        </div>
        <div className="mdr-top-actions no-print">
          {isToday && <button className={`mdr-btn ${live ? 'bdv-live-on' : ''}`} onClick={() => setLive(v => !v)}>
            {live ? <><span className="bdv-dot" /> <Pause size={13} /> Live</> : <><Play size={13} /> Paused</>}</button>}
          <button className="mdr-btn" onClick={() => load().then(() => notify('Punches refreshed'))}><RefreshCw size={14} /> Refresh</button>
          <button className="mdr-btn" disabled={!rows.length} onClick={() => download(`biometric-punches-${f.d}.csv`, toCSV(COLS.map(([label, get]) => ({ label, get })), rows))}><Download size={14} /> Export</button>
        </div>
      </div>

      <div className="md-kpis mds-kpis bdv-kpis three">
        <Kpi label="Punches" value={d.kpis.total} Ico={Fingerprint} tone="blue" note={d.truncated ? 'Latest 500 shown' : 'On the selected day'} />
        <Kpi label="Employees" value={d.kpis.employees} Ico={Users} tone="green" note="Matched to a registration" i={1} />
        <Kpi label="Unmatched" value={d.kpis.unmatched} Ico={CircleAlert} tone="orange" note="PIN belongs to no one" i={2} />
      </div>

      <div className="mdr-card mdr-filters no-print">
        <div className="mdr-search"><Search size={15} /><input placeholder="Search name, employee ID, PIN, device..." value={q} onChange={e => setQ(e.target.value)} />
          {q && <button className="ael-clear" onClick={() => setQ('')} title="Clear search"><X size={13} /></button>}</div>
        <DateInput value={f.d} onChange={e => set('d', e.target.value || iso(new Date()))} />
        <Select value={f.device} onChange={e => set('device', e.target.value)}><option value="">All Devices</option>{d.devices.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}</Select>
        <Select value={f.matched} onChange={e => set('matched', e.target.value)}><option value="">Matched and Unmatched</option><option value="matched">Matched only</option><option value="unmatched">Unmatched only</option></Select>
      </div>

      <div className="mdr-card mdr-table-card">
        <div className="mdr-scroll">
          <table className="mdr-table mds-table ts-table">
            <thead><tr>{COLS.map(([h]) => <th key={h}>{h}</th>)}</tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className={`mdr-row ${fresh.has(r.id) ? 'bdv-fresh' : ''}`}>
                  <td className="mono nowrap"><b>{t12(r.time)}</b></td>
                  <td className="mono nowrap">{r.employee_no || '—'}</td>
                  <td className="nowrap">{r.name ? <b>{r.name}</b> : <Chip tone="orange">Unmatched</Chip>}</td>
                  <td className="nowrap">{r.department || '—'}</td>
                  <td className="mono">{r.pin}</td>
                  <td className="nowrap">{r.device}</td><td className="nowrap">{r.site || '—'}</td>
                  <td><Chip tone={VERIFY_TONE[r.verify] || 'gray'}>{r.verify}</Chip></td>
                  <td className="muted nowrap" title="As the device reported it; not used to decide in or out">{r.device_status || '—'}</td>
                  <td className="nowrap">{r.work_date && r.work_date !== r.date ? <b title="Counts for the day its shift started">{r.work_date}</b> : (r.work_date || '—')}</td>
                  <td className="nowrap" title={r.suspect || r.role || undefined}><Chip tone={attendance(r)[1]}>{attendance(r)[0]}</Chip></td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={COLS.length} className="mdr-empty">{q || f.device || f.matched ? 'No punches match the filters.' : 'No punches on this day.'}</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="ael-pager"><span>{rows.length} punch(es){isToday && live ? ' · refreshes every 10 seconds' : ''}</span></div>
      </div>
    </div>
  );
}
